import type { DocumentKind, DriverAddressInput, DriverPayoutInput, DriverPersonalInput, DriverVehicleInput, RegistrationState } from "@ichegou/contracts";

/**
 * Cadastro de motorista pelo site: `/registrations/driver` for the way in and the driver app's own
 * `/driver/*` routes for the answers, plus the session it opens and the field masks.
 */
export type Registration = RegistrationState;
export type Profile = RegistrationState["profile"];
/** How a code was asked for: the page never learns the phone behind a CPF. */
export type Finder = { phoneNumber: string } | { cpf: string };
export type Channel = "sms" | "whatsapp";
export type City = { id: string; name: string; state: string };
export type PlateFound = { plate: string; make: string; model: string; color: string; year: number | null; isMotorcycle: boolean };
export type PostalCode = { street?: string | null; neighborhood?: string | null };

const BASE = (import.meta.env?.VITE_API_URL as string | undefined) ?? "/api";
const STORE = "ichegou.cadastro-motorista";

export class RegistrationError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message);
  }
}

async function send(method: string, path: string, token: string | null | undefined, body?: BodyInit, type?: string) {
  try {
    return await fetch(`${BASE}${path}`, {
      method,
      // The bearer is the session; a staff member's cookie on this domain must not be.
      credentials: "omit",
      headers: { ...(type ? { "content-type": type } : {}), ...(token ? { authorization: `Bearer ${token}` } : {}) },
      body,
    });
  } catch {
    throw new RegistrationError(0, "offline", "Sem conexão. O que você já salvou continua guardado: tente de novo.");
  }
}

async function read<T>(res: Response): Promise<T> {
  if (res.ok) return (res.status === 200 ? await res.json() : undefined) as T;
  const data = (await res.json().catch(() => null)) as { error?: string; message?: string } | null;
  throw new RegistrationError(res.status, data?.error ?? "unknown", data?.message ?? "Algo deu errado do nosso lado. Tente de novo em instantes.");
}

const call = async <T,>(method: string, path: string, opts: { token?: string | null; body?: unknown } = {}) =>
  read<T>(await send(method, path, opts.token, opts.body === undefined ? undefined : JSON.stringify(opts.body), opts.body === undefined ? undefined : "application/json"));

const P = "/registrations/driver";
export const registrationApi = {
  start: (phoneNumber: string, email: string, channel?: Channel) => call<void>("POST", `${P}/start`, { body: { phoneNumber, email, channel } }),
  resume: (find: Finder, channel?: Channel) => call<void>("POST", `${P}/resume`, { body: { ...find, channel } }),
  verify: (find: Finder, code: string) => call<{ token: string; registration: Registration }>("POST", `${P}/verify`, { body: { ...find, code } }),
  progress: (token: string) => call<Registration>("POST", `${P}/progress`, { token }),
  submit: (token: string) => call<Registration>("POST", `${P}/submit`, { token }),
  unsubscribe: (token: string) => call<void>("POST", `${P}/unsubscribe`, { body: { token } }),
  signOut: (token: string) => call<void>("POST", "/auth/sign-out", { token, body: {} }),

  personal: (token: string, body: DriverPersonalInput) => call<Profile>("PUT", "/driver/onboarding/personal", { token, body }),
  address: (token: string, body: DriverAddressInput) => call<Profile>("PUT", "/driver/onboarding/address", { token, body }),
  vehicle: (token: string, body: DriverVehicleInput) => call<Profile>("PUT", "/driver/onboarding/vehicle", { token, body }),
  payout: (token: string, body: DriverPayoutInput) => call<Profile>("PUT", "/driver/onboarding/payout", { token, body }),
  cities: (token: string) => call<City[]>("GET", "/driver/cities", { token }),
  postalCode: (token: string, cep: string) => call<PostalCode>("GET", `/places/postal-code/${cep}`, { token }),
  plate: (token: string, plate: string) => call<PlateFound>("GET", `/driver/vehicle-lookup/${plate}`, { token }),
  async document(token: string, kind: DocumentKind, file: File) {
    const form = new FormData();
    form.append("file", file);
    return read<Profile>(await send("PUT", `/driver/documents/${kind}`, token, form));
  },
};

export type Saved = { token?: string; find?: Finder; channel?: Channel };

/** Only this tab, gone when it closes: a shared computer shouldn't keep an open account. The phone's code brings it back. */
export const session = {
  read(): Saved {
    try {
      return JSON.parse(sessionStorage.getItem(STORE) ?? "{}") as Saved;
    } catch {
      return {};
    }
  },
  write(value: Saved) {
    try {
      sessionStorage.setItem(STORE, JSON.stringify(value));
    } catch { /* private mode: the form still works until a reload */ }
  },
  clear() {
    try {
      sessionStorage.removeItem(STORE);
    } catch { /* see above */ }
  },
};

export const digits = (s: string) => s.replace(/\D/g, "");

/** "(11) 98765-4321", the parts appearing as they're typed, so backspace never fights the mask. */
export function formatPhone(input: string) {
  let d = digits(input);
  if (d.length > 11 && d.startsWith("55")) d = d.slice(2);
  d = d.slice(0, 11);
  if (d.length <= 2) return d && `(${d}`;
  // A mobile keeps its shape as it grows; a landline's dash stays where it belongs.
  const split = d[2] === "9" ? 7 : 6;
  return `(${d.slice(0, 2)}) ${d.slice(2, split)}${d.length > split ? `-${d.slice(split)}` : ""}`;
}
export const isMobile = (input: string) => /^[1-9]{2}9\d{8}$/.test(digits(input));

/** "529.982.247-25", the same way. */
export function formatCpf(input: string) {
  const d = digits(input).slice(0, 11);
  return [d.slice(0, 3), d.slice(3, 6), d.slice(6, 9)].filter(Boolean).join(".") + (d.length > 9 ? `-${d.slice(9)}` : "");
}

/** Same check as the API's `isValidCpf`: catching a typo here saves a round trip. */
export function isValidCpf(input: string) {
  const d = digits(input);
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const check = (len: number) => {
    let sum = 0;
    for (let i = 0; i < len; i++) sum += Number(d[i]) * (len + 1 - i);
    const r = (sum * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return check(9) === Number(d[9]) && check(10) === Number(d[10]);
}

export const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());

const DOMAIN_TYPOS: Record<string, string> = {
  "gmail.con": "gmail.com", "gmail.co": "gmail.com", "gmial.com": "gmail.com", "gmai.com": "gmail.com", "gamil.com": "gmail.com", "gnail.com": "gmail.com", "gmail.com.br": "gmail.com",
  "hotmail.con": "hotmail.com", "hotmal.com": "hotmail.com", "hotmail.co": "hotmail.com", "hotmai.com": "hotmail.com",
  "outlook.con": "outlook.com", "outlok.com": "outlook.com", "yahoo.con": "yahoo.com", "yaho.com": "yahoo.com", "icloud.con": "icloud.com",
};
/** "ana@gmial.com" → "ana@gmail.com"; null when nothing looks off. */
export function emailSuggestion(email: string) {
  const [local, domain] = email.trim().toLowerCase().split("@");
  const fixed = domain ? DOMAIN_TYPOS[domain] : undefined;
  return local && fixed ? `${local}@${fixed}` : null;
}

/** "12082026"-style typing: "12/08/2026". */
export function formatDate(input: string) {
  const d = digits(input).slice(0, 8);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4)].filter(Boolean).join("/");
}
/** "08/2031". */
export function formatMonth(input: string) {
  const d = digits(input).slice(0, 6);
  return [d.slice(0, 2), d.slice(2)].filter(Boolean).join("/");
}
/** "01310-100". */
export function formatCep(input: string) {
  const d = digits(input).slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}
/** "ABC1D23": what `/driver/onboarding/vehicle` stores, whichever way it's typed. */
export const formatPlate = (input: string) => input.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7);
export const isPlate = (plate: string) => /^[A-Z]{3}\d[A-Z0-9]\d{2}$/.test(plate);

/** "31/12/1990" → "1990-12-31"; null when it isn't a real day. */
export function isoDay(text: string) {
  const m = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const iso = `${m[3]}-${m[2]}-${m[1]}`;
  const date = new Date(`${iso}T12:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === iso ? iso : null;
}
/** "08/2031" → "2031-08"; null when the month doesn't exist. */
export function isoMonth(text: string) {
  const m = text.match(/^(\d{2})\/(\d{4})$/);
  return m && Number(m[1]) >= 1 && Number(m[1]) <= 12 ? `${m[2]}-${m[1]}` : null;
}
/** Whole years between a birth day and today, both ISO. */
export function ageOn(birth: string, today: string) {
  return Number(today.slice(0, 4)) - Number(birth.slice(0, 4)) - (today.slice(5) < birth.slice(5) ? 1 : 0);
}
export const displayDay = (iso: string) => iso.split("-").reverse().join("/");
export const displayMonth = (iso: string) => `${iso.slice(5, 7)}/${iso.slice(0, 4)}`;

/** Every step on the progress bar; the phone's code is part of Contato. */
export const STEPS = ["Contato", "Seus dados", "Endereço", "Veículo", "Documentos", "Recebimento", "Revisão"] as const;
export const STEP_HINTS: Record<(typeof STEPS)[number], string> = {
  Contato: "Celular e e-mail",
  "Seus dados": "Nome, CPF e CNH",
  Endereço: "Onde você mora e vai dirigir",
  Veículo: "Placa, modelo e ano",
  Documentos: "CNH e documento do veículo",
  Recebimento: "Sua chave Pix",
  Revisão: "Confira tudo e envie",
};

/** Same formats as `/driver/documents/:kind`. */
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const DOCUMENT_TEXT: Record<DocumentKind, { title: string; hint: string; accept: string }> = {
  profile_photo: { title: "Foto de perfil", hint: "Rosto centralizado, sem óculos escuros ou boné. Passageiros veem esta foto.", accept: "image/*" },
  cnh_front: { title: "CNH · frente", hint: "CNH digital ou foto sem plástico, com tudo legível.", accept: "image/*,application/pdf" },
  cnh_back: { title: "CNH · verso", hint: "Também sem plástico, com tudo legível.", accept: "image/*,application/pdf" },
  crlv: { title: "CRLV-e", hint: "PDF do app Carteira Digital de Trânsito.", accept: "image/*,application/pdf" },
  background_state: { title: "Antecedentes · estadual", hint: "Certidão criminal do seu estado.", accept: "image/*,application/pdf" },
  background_federal: { title: "Antecedentes · federal", hint: "Certidão da Polícia Federal.", accept: "image/*,application/pdf" },
};
